import React, { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';

const BottomPanel = ({ shipment, sensorData, anomalies }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('temperature');
  const [viewMode, setViewMode] = useState('readings'); // 'readings' or 'journey'

  // Get available sensor types based on data
  const getAvailableTabs = () => {
    if (!sensorData) {
      console.log('getAvailableTabs: No sensorData');
      return [];
    }
    
    console.log('getAvailableTabs: sensorData keys:', Object.keys(sensorData));
    console.log('getAvailableTabs: temperature_1 exists:', !!sensorData.temperature_1);
    console.log('getAvailableTabs: temperature_1 length:', sensorData.temperature_1?.length);
    
    const availableTabs = [];
    
    // Check for temperature sensors
    if ((sensorData.temperature_1 && sensorData.temperature_1.length > 0) || (sensorData.temperature_2 && sensorData.temperature_2.length > 0)) {
      availableTabs.push({ 
        id: 'temperature', 
        label: 'Temperature', 
        unit: '°C', 
        color: '#ef4444', 
        internal: 'temperature_1', 
        external: (sensorData.temperature_2 && sensorData.temperature_2.length > 0) ? 'temperature_2' : null
      });
    }
    
    // Check for humidity sensors
    if ((sensorData.humidity_1 && sensorData.humidity_1.length > 0) || (sensorData.humidity_2 && sensorData.humidity_2.length > 0)) {
      availableTabs.push({ 
        id: 'humidity', 
        label: 'Humidity', 
        unit: '%', 
        color: '#3b82f6', 
        internal: 'humidity_1', 
        external: (sensorData.humidity_2 && sensorData.humidity_2.length > 0) ? 'humidity_2' : null
      });
    }
    
    // Check for pressure sensors
    if ((sensorData.pressure_1 && sensorData.pressure_1.length > 0) || (sensorData.pressure_2 && sensorData.pressure_2.length > 0)) {
      availableTabs.push({ 
        id: 'pressure', 
        label: 'Pressure', 
        unit: 'hPa', 
        color: '#8b5cf6', 
        internal: 'pressure_1', 
        external: (sensorData.pressure_2 && sensorData.pressure_2.length > 0) ? 'pressure_2' : null
      });
    }
    
    // Check for other sensors only if they have data
    if (sensorData.light && sensorData.light.length > 0) {
      availableTabs.push({ id: 'light', label: 'Light', unit: 'lux', color: '#f59e0b' });
    }
    
    if (sensorData.motion && sensorData.motion.length > 0) {
      availableTabs.push({ id: 'motion', label: 'Motion', unit: '', color: '#10b981' });
    }
    
    if (sensorData.gas && sensorData.gas.length > 0) {
      availableTabs.push({ id: 'gas', label: 'Gas', unit: '', color: '#06b6d4' });
    }
    
    return availableTabs;
  };
  
  const tabs = getAvailableTabs();

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString();
  };

  const getAnomalyLines = () => {
    if (!anomalies || !Array.isArray(anomalies)) return [];
    
    // Filter and deduplicate anomalies
    const filteredAnomalies = anomalies
      .filter(anomaly => {
        // Only show anomalies that match the current sensor type
        const sensorType = activeTab;
        return anomaly.type.includes(sensorType) || 
               (sensorType === 'temperature' && anomaly.type.includes('temperature')) ||
               (sensorType === 'humidity' && anomaly.type.includes('humidity')) ||
               (sensorType === 'pressure' && anomaly.type.includes('pressure')) ||
               (sensorType === 'light' && anomaly.type.includes('light')) ||
               (sensorType === 'motion' && anomaly.type.includes('motion')) ||
               (sensorType === 'gas' && anomaly.type.includes('gas'));
      })
      .map(anomaly => ({
        x: new Date(anomaly.timestamp).getTime(),
        stroke: anomaly.severity === 'critical' ? '#ef4444' : '#f59e0b',
        strokeDasharray: '5 5',
        label: anomaly.type.replace('_', ' ').toUpperCase()
      }));
    
    // Deduplicate by timestamp (only show one anomaly per time point)
    const uniqueAnomalies = [];
    const seenTimestamps = new Set();
    
    filteredAnomalies.forEach(anomaly => {
      if (!seenTimestamps.has(anomaly.x)) {
        uniqueAnomalies.push(anomaly);
        seenTimestamps.add(anomaly.x);
      }
    });
    
    return uniqueAnomalies;
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="text-sm font-medium text-gray-900">
            {formatTime(label)}
          </p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm text-gray-600">
              {entry.name}: {entry.value}{tabs.find(tab => tab.id === entry.dataKey)?.unit}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const getCurrentReadings = () => {
    const sensorDataForTab = getSensorDataForTab(activeTab);
    if (sensorDataForTab.length === 0) return [];
    
    // Return just the latest reading
    const latest = sensorDataForTab[sensorDataForTab.length - 1];
    return [latest];
  };

  const getSensorDataForTab = (tabId) => {
    if (!sensorData) {
      return [];
    }
    
    // Check if sensorData is an array (wrong structure) or object (correct structure)
    if (Array.isArray(sensorData)) {
      return [];
    }
    
    // The sensorData is already the shipment's sensor data object
    // (it was passed as sensorData[selectedShipment?.id] from App.js)
    const shipmentSensorData = sensorData;
    if (!shipmentSensorData) return [];
    
    const tab = tabs.find(t => t.id === tabId);
    if (!tab) return [];
    
    // Check if we have internal/external sensors
    if (tab.internal && tab.external) {
      // Combine internal and external sensor data
      const internalData = shipmentSensorData[tab.internal] || [];
      const externalData = shipmentSensorData[tab.external] || [];
      
      // Merge the data and add sensor type
      const combinedData = [];
      
      internalData.forEach(item => {
        combinedData.push({
          ...item,
          sensor: 'Internal',
          sensorType: 'internal'
        });
      });
      
      externalData.forEach(item => {
        combinedData.push({
          ...item,
          sensor: 'External',
          sensorType: 'external'
        });
      });
      
      // Sort by time
      combinedData.sort((a, b) => new Date(a.time) - new Date(b.time));
      
      return combinedData;
    } else if (tab.internal) {
      // Only internal sensor (no external)
      const internalData = shipmentSensorData[tab.internal] || [];
      return internalData.map(item => ({
        ...item,
        sensor: 'Internal',
        sensorType: 'internal'
      }));
    } else {
      // Single sensor type
      return shipmentSensorData[tabId] || [];
    }
  };

  const getJourneyData = () => {
    const sensorDataForTab = getSensorDataForTab(activeTab);
    
    if (sensorDataForTab.length === 0) return [];
    
    // Format data for Recharts - convert time to timestamp for X-axis
    const formattedData = sensorDataForTab.map(item => ({
      ...item,
      time: new Date(item.time).getTime() // Convert to timestamp for chart
    }));
    
    return formattedData;
  };

  const getDisplayData = () => {
    const data = viewMode === 'readings' ? getCurrentReadings() : getJourneyData();
    return data;
  };

  // Debug sensor data structure (commented out for production)
  console.log('BottomPanel: Shipment ID:', shipment?.id);
  if (sensorData) {
    console.log('BottomPanel: Sensor data keys:', Object.keys(sensorData));
    console.log('BottomPanel: Available sensor types:', Object.keys(sensorData || {}));
    console.log('BottomPanel: Temperature data:', sensorData.temperature_1);
    console.log('BottomPanel: Tabs:', tabs);
  } else {
    console.log('BottomPanel: No sensor data for shipment:', shipment?.id);
  }

  if (!sensorData) {
    return (
      <div className="h-16 bg-white border-t border-gray-200 flex items-center justify-center">
        <p className="text-gray-500">No sensor data available for this shipment</p>
      </div>
    );
  }

  return (
    <div className={`bg-white border-t border-gray-200 transition-all duration-300 ${
      isCollapsed ? 'h-16' : 'h-80'
    }`}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900">Sensor Data</h3>
        <div className="flex items-center space-x-4">
          {/* View Mode Toggle */}
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600">View:</span>
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('readings')}
                className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                  viewMode === 'readings'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Latest
              </button>
              <button
                onClick={() => setViewMode('journey')}
                className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                  viewMode === 'journey'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Journey
              </button>
            </div>
          </div>
          
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            {isCollapsed ? (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
              </svg>
            )}
          </button>
        </div>
      </div>

      {!isCollapsed && (
        <div className="flex-1 flex flex-col">
          {/* Tabs */}
          <div className="flex border-b border-gray-200">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'text-blue-600 border-b-2 border-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Chart or Current Reading */}
          <div className="flex-1 p-4">
            {viewMode === 'readings' ? (
              // Current Readings View
              <div className="h-full flex items-center justify-center">
                <div className="text-center">
                  {getDisplayData().length > 0 ? (
                    <>
                      {/* Check if we have both internal and external sensors */}
                      {sensorData && sensorData[tabs.find(tab => tab.id === activeTab)?.internal] && 
                       sensorData[tabs.find(tab => tab.id === activeTab)?.external] ? (
                        // Show both sensors side by side
                        <div className="flex space-x-8">
                          {/* Internal sensor reading */}
                          <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                            <div className="text-2xl font-medium text-gray-600 mb-2">
                              Internal
                            </div>
                            <div className="text-5xl font-bold text-gray-900 mb-2">
                              {sensorData[tabs.find(tab => tab.id === activeTab)?.internal]?.[sensorData[tabs.find(tab => tab.id === activeTab)?.internal]?.length - 1]?.value?.toFixed(2) || 'N/A'}{tabs.find(tab => tab.id === activeTab)?.unit}
                            </div>
                            <div className="text-sm text-gray-500">
                              {tabs.find(tab => tab.id === activeTab)?.label}
                            </div>
                          </div>
                          
                          {/* External sensor reading */}
                          <div className="bg-green-50 rounded-lg p-6 border border-green-200">
                            <div className="text-2xl font-medium text-green-700 mb-2">
                              External
                            </div>
                            <div className="text-5xl font-bold text-green-600 mb-2">
                              {sensorData[tabs.find(tab => tab.id === activeTab)?.external]?.[sensorData[tabs.find(tab => tab.id === activeTab)?.external]?.length - 1]?.value?.toFixed(2) || 'N/A'}{tabs.find(tab => tab.id === activeTab)?.unit}
                            </div>
                            <div className="text-sm text-green-600">
                              {tabs.find(tab => tab.id === activeTab)?.label}
                            </div>
                          </div>
                        </div>
                      ) : (
                        // Show single sensor (either internal, external, or fallback)
                        <>
                          {/* Internal sensor reading */}
                          {sensorData && sensorData[tabs.find(tab => tab.id === activeTab)?.internal] && (
                            <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                              <div className="text-2xl font-medium text-gray-600 mb-2">
                                Internal
                              </div>
                              <div className="text-5xl font-bold text-gray-900 mb-2">
                                {sensorData[tabs.find(tab => tab.id === activeTab)?.internal]?.[sensorData[tabs.find(tab => tab.id === activeTab)?.internal]?.length - 1]?.value?.toFixed(2) || 'N/A'}{tabs.find(tab => tab.id === activeTab)?.unit}
                              </div>
                              <div className="text-sm text-gray-500">
                                {tabs.find(tab => tab.id === activeTab)?.label}
                              </div>
                            </div>
                          )}
                          
                          {/* External sensor reading */}
                          {sensorData && sensorData[tabs.find(tab => tab.id === activeTab)?.external] && (
                            <div className="bg-green-50 rounded-lg p-6 border border-green-200">
                              <div className="text-2xl font-medium text-green-700 mb-2">
                                External
                              </div>
                              <div className="text-5xl font-bold text-green-600 mb-2">
                                {sensorData[tabs.find(tab => tab.id === activeTab)?.external]?.[sensorData[tabs.find(tab => tab.id === activeTab)?.external]?.length - 1]?.value?.toFixed(2) || 'N/A'}{tabs.find(tab => tab.id === activeTab)?.unit}
                              </div>
                              <div className="text-sm text-green-600">
                                {tabs.find(tab => tab.id === activeTab)?.label}
                              </div>
                            </div>
                          )}
                          
                          {/* Single sensor reading (fallback) */}
                          {sensorData && sensorData[activeTab] && (
                            <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
                              <div className="text-2xl font-medium text-blue-700 mb-2">
                                Current Reading
                              </div>
                              <div className="text-5xl font-bold text-blue-600 mb-2">
                                {sensorData[activeTab]?.[sensorData[activeTab]?.length - 1]?.value?.toFixed(2) || 'N/A'}{tabs.find(tab => tab.id === activeTab)?.unit}
                              </div>
                              <div className="text-sm text-blue-600">
                                {tabs.find(tab => tab.id === activeTab)?.label}
                              </div>
                            </div>
                          )}
                        </>
                      )}
                      
                      {getDisplayData()[0] && (
                        <div className="text-xs text-gray-400 mt-4">
                          Last updated: {formatTime(getDisplayData()[0].time)}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-gray-500">
                      No data available for {tabs.find(tab => tab.id === activeTab)?.label}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              // Journey Chart View
              <div className="h-full">
                {getDisplayData().length > 0 ? (
                  <div className="h-full">
                    <div style={{ height: '300px', width: '100%' }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart
                          data={getDisplayData()}
                          margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis
                            dataKey="time"
                            tickFormatter={formatTime}
                            stroke="#6b7280"
                            fontSize={12}
                            type="number"
                            domain={['dataMin', 'dataMax']}
                          />
                          <YAxis
                            stroke="#6b7280"
                            fontSize={12}
                            unit={tabs.find(tab => tab.id === activeTab)?.unit}
                          />
                          <Tooltip content={<CustomTooltip />} />
                          {/* Internal sensor line */}
                          {getDisplayData().filter(d => d.sensorType === 'internal').length > 0 && (
                            <Line
                              type="monotone"
                              dataKey="value"
                              stroke={tabs.find(tab => tab.id === activeTab)?.color}
                              strokeWidth={2}
                              dot={{ fill: tabs.find(tab => tab.id === activeTab)?.color, strokeWidth: 2, r: 3 }}
                              activeDot={{ r: 5, stroke: tabs.find(tab => tab.id === activeTab)?.color, strokeWidth: 2 }}
                              name="Internal"
                              data={getDisplayData().filter(d => d.sensorType === 'internal')}
                            />
                          )}
                          
                          {/* External sensor line */}
                          {getDisplayData().filter(d => d.sensorType === 'external').length > 0 && (
                            <Line
                              type="monotone"
                              dataKey="value"
                              stroke="#10b981"
                              strokeWidth={2}
                              dot={{ fill: "#10b981", strokeWidth: 2, r: 3 }}
                              activeDot={{ r: 5, stroke: "#10b981", strokeWidth: 2 }}
                              name="External"
                              data={getDisplayData().filter(d => d.sensorType === 'external')}
                            />
                          )}
                          
                          {/* Single sensor line (fallback) */}
                          {getDisplayData().filter(d => !d.sensorType).length > 0 && (
                            <Line
                              type="monotone"
                              dataKey="value"
                              stroke={tabs.find(tab => tab.id === activeTab)?.color}
                              strokeWidth={2}
                              dot={{ fill: tabs.find(tab => tab.id === activeTab)?.color, strokeWidth: 2, r: 3 }}
                              activeDot={{ r: 5, stroke: tabs.find(tab => tab.id === activeTab)?.color, strokeWidth: 2 }}
                            />
                          )}
                          
                          {/* Anomaly reference lines */}
                          {getAnomalyLines().map((line, index) => (
                            <ReferenceLine
                              key={index}
                              x={line.x}
                              stroke={line.stroke}
                              strokeDasharray={line.strokeDasharray}
                              label={line.label}
                            />
                          ))}
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                ) : (
                  <div className="h-full flex items-center justify-center">
                    <div className="text-center text-gray-500">
                      <div className="text-2xl mb-2">📊</div>
                      <p>No chart data available for {activeTab}</p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

                    {/* Summary Stats */}
          <div className="p-4 border-t border-gray-200 bg-gray-50">
            <div className="mb-3">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Sensor Summary</h3>
            </div>
            <div className="grid grid-cols-3 gap-6">
              {tabs.map((tab) => {
                const data = getSensorDataForTab(tab.id);
                if (!data || data.length === 0) return null;
                
                const internalData = data.filter(d => d.sensorType === 'internal');
                const externalData = data.filter(d => d.sensorType === 'external');
                const singleData = data.filter(d => !d.sensorType);
                
                // Handle different sensor types
                if (internalData.length > 0 && externalData.length > 0) {
                  // Both internal and external sensors
                  const internalValues = internalData.map(d => d.value);
                  const externalValues = externalData.map(d => d.value);
                  const internalCurrent = internalValues[internalValues.length - 1];
                  const externalCurrent = externalValues[externalValues.length - 1];
                  const internalMin = Math.min(...internalValues);
                  const internalMax = Math.max(...internalValues);
                  const externalMin = Math.min(...externalValues);
                  const externalMax = Math.max(...externalValues);
                  
                  return (
                    <div key={tab.id} className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                      <div className="text-sm font-medium text-gray-700 mb-3">{tab.label}</div>
                      
                      {/* Internal sensor */}
                      <div className="mb-3">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs text-gray-500">Internal</span>
                          <span className="text-sm font-semibold text-gray-900">
                            {internalCurrent.toFixed(2)}{tab.unit}
                          </span>
                        </div>
                        <div className="text-xs text-gray-400">
                          Range: {internalMin.toFixed(2)} - {internalMax.toFixed(2)}
                        </div>
                      </div>
                      
                      {/* External sensor */}
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs text-green-600">External</span>
                          <span className="text-sm font-semibold text-green-700">
                            {externalCurrent.toFixed(2)}{tab.unit}
                          </span>
                        </div>
                        <div className="text-xs text-green-500">
                          Range: {externalMin.toFixed(2)} - {externalMax.toFixed(2)}
                        </div>
                      </div>
                    </div>
                  );
                } else if (internalData.length > 0) {
                  // Only internal sensor (no external)
                  const values = internalData.map(d => d.value);
                  const min = Math.min(...values);
                  const max = Math.max(...values);
                  const current = values[values.length - 1];
                  return (
                    <div key={tab.id} className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                      <div className="text-sm font-medium text-gray-700 mb-3">{tab.label}</div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-gray-500">Internal</span>
                        <span className="text-sm font-semibold text-gray-900">
                          {current.toFixed(2)}{tab.unit}
                        </span>
                      </div>
                      <div className="text-xs text-gray-400">
                        Range: {min.toFixed(2)} - {max.toFixed(2)}
                      </div>
                    </div>
                  );
                } else if (singleData.length > 0) {
                  // Single sensor type
                  const values = singleData.map(d => d.value);
                  const min = Math.min(...values);
                  const max = Math.max(...values);
                  const current = values[values.length - 1];
                  
                  return (
                    <div key={tab.id} className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                      <div className="text-sm font-medium text-gray-700 mb-3">{tab.label}</div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-gray-500">Current</span>
                        <span className="text-sm font-semibold text-gray-900">
                          {current.toFixed(2)}{tab.unit}
                        </span>
                      </div>
                      <div className="text-xs text-gray-400">
                        Range: {min.toFixed(2)} - {max.toFixed(2)}
                      </div>
                    </div>
                  );
                }
                
                return null;
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BottomPanel; 